import { useVinCodeService } from '../../services/vinCodeService';
import { useEffect, useState, useMemo, Fragment } from 'react';
import { Table, Button, Spin, Empty, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCredits } from '../../context/CreditsContext';
import classes from './styles.module.scss';
import vinClasses from '../../common/styles/vinHighlighting.module.scss';
import { colors, primitiveColors, radius } from '../../theming';
import { useNavigate } from 'react-router-dom';
import { TableCellText } from '../../common/components/TableCellText/TableCellText';
import capitalize from '../../utils/capitalize';
import { datesFormat } from '../../common/constants/datesFormat';
import dayjs from 'dayjs';
import { CONDITIONS, getViewButtonState } from './helper';
import ReactGA from 'react-ga4';
import { googleAnalyticsEvents } from '../../common/constants/googleAnalyticsEvents';
import { usePaginationConfig } from '../../hooks/usePaginationConfig';
import { ShareAltOutlined, CheckOutlined } from '@ant-design/icons';
import globalNotification from '../../common/hooks/globalNotification';
import { notificationType } from '../../common/constants/notificationType';
import { EyeOutlined, CloseOutlined } from '@ant-design/icons';
import { TYPE_MAP } from '../../common/constants/reportPlans';
import { useMediaQuery } from '@uidotdev/usehooks';

const VIEW_BUTTON_ICONS = { viewable: <EyeOutlined />, rejected: <CloseOutlined />, pending: null };

// rejected jobs keep their status color; viewable ones use the brand color
const getViewButtonStyle = ({ tone }, record) => ({
    backgroundColor:
        tone === 'viewable' ? primitiveColors.main : tone === 'rejected' ? record?.status?.primaryColor : 'transparent',
    borderRadius: radius.huge,
    border: tone === 'pending' ? undefined : `1px solid ${primitiveColors.main}`,
    color: tone === 'pending' ? primitiveColors.gray500 : colors.white,
});

const VinCodeList = ({ searchString, consumerId }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paginationState, setPaginationState] = useState({
        currentPage: +(localStorage.getItem('reportTablePaginationPage') || 1),
        pageSize: +(localStorage.getItem('reportTablePaginationPageSize') || 5),
    });
    const [copyStates, setCopyStates] = useState({});
    const isMobile = useMediaQuery('only screen and (max-width : 480px)');

    const { getVinCodesByConsumerId } = useVinCodeService();
    const { credits } = useCredits();
    const { t, ready, i18n } = useTranslation(['profile', 'transactions', 'jobs', 'carReport']);
    const navigate = useNavigate();

    const currentLanguage = i18n.language;

    const handleTableChange = pagination => {
        setPaginationState({
            currentPage: pagination.current,
            pageSize: pagination.pageSize,
        });
    };

    const paginationConfig = usePaginationConfig({
        currentPage: paginationState.currentPage,
        pageSize: paginationState.pageSize,
        total: data.length,
        onChange: handleTableChange,
        localStorageKey: 'reportTablePagination',
        showSizeChanger: data.length > 5,
        className: classes['table-wrapper-pagination'],
    });

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                const { data } = await getVinCodesByConsumerId(consumerId, searchString);
                setData(data);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 500);
            }
        };

        fetchData();
    }, [consumerId, credits, searchString]);

    const viewReport = (e, record) => {
        e.preventDefault();

        if (record.type === 'combined') {
            navigate(`/combined-report/${record.vin}/${record.uuid}`, { state: { record } });
            ReactGA.event(googleAnalyticsEvents.VIEW_REPORT_CLICK);
            return;
        }

        if (record.type === 'internal') {
            navigate(`/internal-report/${record.vin}`, { state: { record } });
            ReactGA.event(googleAnalyticsEvents.VIEW_REPORT_CLICK);
            return;
        }

        // a dealer report opens on our report page even before (or without) a parsed report: the page shows the
        // original PDF, the waiting state or the dealer's answer in its place
        if (CONDITIONS.HAS_FILES(record) || CONDITIONS.IS_DONE(record)) {
            navigate(`/report/${record.uuid}`, { state: { record } });
        }

        ReactGA.event(googleAnalyticsEvents.VIEW_REPORT_CLICK);
    };

    const handleShareReport = async (e, record) => {
        e.preventDefault();
        e.stopPropagation();

        const recordId = record.uuid;
        const sharedLink = record.sharedLink;
        setCopyStates(prev => ({ ...prev, [recordId]: { isCopying: true, copySuccess: false } }));
        try {
            const reportUrl = `${window.location.origin}/shared-report/${sharedLink}`;
            await navigator.clipboard.writeText(reportUrl);

            setCopyStates(prev => ({ ...prev, [recordId]: { isCopying: false, copySuccess: true } }));
            globalNotification.open({
                type: notificationType.SUCCESS,
                message: t('carReport:shareReport.success'),
                description: t('carReport:shareReport.linkCopied'),
            });

            setTimeout(() => {
                setCopyStates(prev => ({ ...prev, [recordId]: { isCopying: false, copySuccess: false } }));
            }, 1000);
        } catch (error) {
            console.error('Error sharing report:', error);
            setCopyStates(prev => ({ ...prev, [recordId]: { isCopying: false, copySuccess: false } }));
            globalNotification.open({
                type: notificationType.ERROR,
                message: t('carReport:shareReport.error'),
                description: t('carReport:shareReport.copyFailed'),
            });
        }
    };

    const columns = useMemo(
        () => [
            {
                title: t('profile:vin'),
                dataIndex: 'vin',
                key: 'vin',
                render: vin => <TableCellText text={vin} className={vinClasses.vinHighlight} />,
            },
            {
                title: t('profile:brand'),
                dataIndex: 'brand',
                key: 'brand',
                render: brand => <TableCellText text={brand} />,
            },
            { title: t('profile:year'), dataIndex: 'year', key: 'year', render: year => <TableCellText text={year} /> },
            {
                title: t('profile:region'),
                dataIndex: 'region',
                width: 200,
                key: 'region',
                render: region => <TableCellText text={region} />,
            },
            {
                title: t('profile:created-at'),
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: createdAt => (
                    <TableCellText
                        text={capitalize(
                            dayjs(createdAt)
                                .locale(currentLanguage)
                                .format(currentLanguage === 'uk' ? datesFormat.uk.long : datesFormat.en.short),
                            currentLanguage === 'uk' ? 3 : 0
                        )}
                    />
                ),
            },
            {
                title: t('profile:type.title'),
                dataIndex: 'type',
                key: 'type',
                render: type => {
                    const mappedType = TYPE_MAP[type] ?? type;

                    return <TableCellText text={t(`profile:type.${mappedType}`)} />;
                },
            },
            {
                title: '',
                fixed: 'right',
                width: isMobile ? 80 : 150,
                key: 'action',
                render: record => {
                    const recordId = record.uuid;
                    const copyState = copyStates[recordId] || { isCopying: false, copySuccess: false };
                    const viewButton = getViewButtonState(record);

                    if (isMobile) {
                        return (
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center' }}>
                                <Button
                                    icon={copyState.copySuccess ? <CheckOutlined /> : <ShareAltOutlined />}
                                    onClick={e => handleShareReport(e, record)}
                                    loading={copyState.isCopying}
                                    disabled={!CONDITIONS.IS_DONE(record) || !record?.sharedLink}
                                    size="small"
                                    type="text"
                                    style={{
                                        color: copyState.copySuccess ? '#52c41a' : primitiveColors.gray500,
                                        backgroundColor: copyState.copySuccess ? '#f6ffed' : 'transparent',
                                        border: 'none',
                                        transition: 'all 0.2s ease-in-out',
                                        transform: copyState.copySuccess ? 'scale(1.1)' : 'scale(1)',
                                        minWidth: '32px',
                                        height: '32px',
                                    }}
                                    title={t('carReport:shareReport.button')}
                                />
                                <Button
                                    icon={
                                        viewButton.showSpinner ? (
                                            <Spin className={classes['progress-btn-spinner']} size="small" />
                                        ) : (
                                            VIEW_BUTTON_ICONS[viewButton.tone]
                                        )
                                    }
                                    onClick={e => viewReport(e, record)}
                                    disabled={viewButton.disabled}
                                    size="small"
                                    type="text"
                                    style={{
                                        ...getViewButtonStyle(viewButton, record),
                                        minWidth: '32px',
                                        height: '32px',
                                    }}
                                    title={t(viewButton.labelKey)}
                                />
                            </div>
                        );
                    }

                    return (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Button
                                icon={copyState.copySuccess ? <CheckOutlined /> : <ShareAltOutlined />}
                                onClick={e => handleShareReport(e, record)}
                                loading={copyState.isCopying}
                                disabled={!CONDITIONS.IS_DONE(record) || !record?.sharedLink}
                                size="small"
                                type="text"
                                style={{
                                    color: copyState.copySuccess ? '#52c41a' : primitiveColors.gray500,
                                    backgroundColor: copyState.copySuccess ? '#f6ffed' : 'transparent',
                                    border: 'none',
                                    transition: 'all 0.2s ease-in-out',
                                    transform: copyState.copySuccess ? 'scale(1.1)' : 'scale(1)',
                                    minWidth: '32px',
                                    height: '32px',
                                }}
                                title={t('carReport:shareReport.button')}
                            />
                            <Button
                                onClick={e => viewReport(e, record)}
                                disabled={viewButton.disabled}
                                style={{ ...getViewButtonStyle(viewButton, record), flex: 1 }}
                            >
                                {viewButton.showSpinner && (
                                    <Spin className={classes['progress-btn-spinner']} size="small" />
                                )}
                                {t(viewButton.labelKey)}
                            </Button>
                        </div>
                    );
                },
            },
        ],
        [ready, data, i18n.language, searchString, copyStates, isMobile]
    );

    return (
        <Fragment>
            {loading ? (
                <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
                <Table
                    className={classes['table-wrapper']}
                    columns={columns}
                    dataSource={data}
                    pagination={paginationConfig}
                    onChange={handleTableChange}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: <Empty description={t('general:no-data')} /> }}
                />
            )}
        </Fragment>
    );
};

export default VinCodeList;
